/**
 * World clock panel — multi-timezone display.
 *
 * Hardcoded zones: Washington DC, London, Brussels, Moscow,
 * Jerusalem, Beijing, Tokyo. Updates every second.
 * Compact 2-column grid layout.
 */

const ZONES: { city: string; tz: string; utcLabel: string }[] = [
  { city: 'Washington DC', tz: 'America/New_York', utcLabel: 'UTC-5/-4' },
  { city: 'London', tz: 'Europe/London', utcLabel: 'UTC+0/+1' },
  { city: 'Brussels', tz: 'Europe/Brussels', utcLabel: 'UTC+1/+2' },
  { city: 'Moscow', tz: 'Europe/Moscow', utcLabel: 'UTC+3' },
  { city: 'Jerusalem', tz: 'Asia/Jerusalem', utcLabel: 'UTC+2/+3' },
  { city: 'Beijing', tz: 'Asia/Shanghai', utcLabel: 'UTC+8' },
  { city: 'Tokyo', tz: 'Asia/Tokyo', utcLabel: 'UTC+9' },
];

export function createWorldClockPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'world-clock-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto; padding: 12px;';

  // Title
  const title = document.createElement('div');
  title.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em;';
  title.textContent = 'World Clock';
  container.appendChild(title);

  // Grid container
  const grid = document.createElement('div');
  grid.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 8px;';
  container.appendChild(grid);

  // Create cards (persistent DOM elements updated by render)
  const cards: { card: HTMLElement; timeEl: HTMLElement; dateEl: HTMLElement; offsetEl: HTMLElement; iconEl: HTMLElement }[] = [];

  for (const zone of ZONES) {
    const card = document.createElement('div');
    card.style.cssText = `
      padding: 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    // City name
    const cityEl = document.createElement('div');
    cityEl.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;';
    cityEl.textContent = zone.city;
    card.appendChild(cityEl);

    // Time display
    const timeEl = document.createElement('div');
    timeEl.style.cssText = 'font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: var(--text-primary); letter-spacing: 1px;';
    card.appendChild(timeEl);

    // Date + offset row
    const metaRow = document.createElement('div');
    metaRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 4px;';

    const dateEl = document.createElement('span');
    dateEl.style.cssText = 'font-size: 10px; color: var(--text-muted);';
    metaRow.appendChild(dateEl);

    const offsetEl = document.createElement('span');
    offsetEl.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-left: auto; font-family: var(--font-mono);';
    offsetEl.textContent = zone.utcLabel;
    metaRow.appendChild(offsetEl);

    // Day/night icon
    const iconEl = document.createElement('span');
    iconEl.style.cssText = 'font-size: 12px;';
    metaRow.appendChild(iconEl);

    card.appendChild(metaRow);

    grid.appendChild(card);
    cards.push({ card, timeEl, dateEl, offsetEl, iconEl });
  }

  const cleanupFns: (() => void)[] = [];

  // Pre-build formatters once (avoids 21 allocations/sec)
  const formatters = ZONES.map(zone => ({
    time: new Intl.DateTimeFormat('en-GB', {
      timeZone: zone.tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }),
    date: new Intl.DateTimeFormat('en-GB', {
      timeZone: zone.tz, weekday: 'short', day: 'numeric', month: 'short',
    }),
    hour: new Intl.DateTimeFormat('en-GB', {
      timeZone: zone.tz, hour: 'numeric', hour12: false,
    }),
  }));

  function render(): void {
    const now = new Date();

    for (let i = 0; i < ZONES.length; i++) {
      const info = cards[i];
      const fmt = formatters[i];

      info.timeEl.textContent = fmt.time.format(now);
      info.dateEl.textContent = fmt.date.format(now);

      const hour = parseInt(fmt.hour.format(now), 10);
      const isDay = hour >= 6 && hour < 18;

      info.iconEl.textContent = isDay ? '\u2600' : '\u263D';
      info.iconEl.title = isDay ? 'Daytime' : 'Nighttime';
    }
  }

  // Initial render
  render();

  // Update every second
  const intervalId = setInterval(render, 1000);
  cleanupFns.push(() => clearInterval(intervalId));

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
